import React, { useState, useEffect } from 'react'

import {
  getAuthManagerInstance,
  logOff,
  isInstitutional,
  isInvestor,
  isLoggedIn,
} from '../../global/authentication/authentication-manager.js'
import {
  getAdvisorPathIfLocal,
  getInstitutionalPathIfLocal,
  getInvestorPathIfLocal,
} from '../../global/environments/index.js'
import { isLocal } from '../../spa/utility'
import { useRef } from 'react'
import { getAbsolutePath } from '../../global/environments/path.js'

import LoggedInTray from './ConsultantContact/LoggedInTray.jsx'
import LoggedOutTray from './ConsultantContact/LoggedOutTray.jsx'
import {
  isInstitutionalLoggedIn,
  logoutInstitutional,
  accessStorage,
} from '../../spa/components/institutional/common/institutionalConfig.js'

import DOMPurify from 'dompurify'

export const DATA = {
  FIRST_TIME_VISITOR: {
    welcomeMsg:
      '<span class="u-textBold u-sm-block">Welcome to Columbia Threadneedle.</span> Please select your role:',
  },
  ADVISOR: {
    welcomeMsg:
      '<span class="u-textBold">Welcome</span> to our site for financial professionals',
    roleTitle: 'financial professional',
    smallicon: `${
      isLocal() ? '/cti-site' : ''
    }/webfiles/latest/assets/cti/images/icon_advisor_site.svg`,
    icon: `${
      isLocal() ? '/cti-site' : ''
    }/webfiles/latest/assets/cti/images/icon_advisor_illustration.svg`,
    title: 'Financial professional',
    description: 'You manage client investments',
    buttonLabel: 'VIEW THIS SITE',
    buttonSrc: getAdvisorPathIfLocal() || '/',
    linkLabel: 'Website log in',
    linkSrc: getAdvisorPathIfLocal() + '/login',
    userPrefKey: 'FP-Advisor',
    style: {
      topBarBgImg: `${
        isLocal() ? '/cti-site' : ''
      }/webfiles/latest/assets/cti/images/brand-texture_banner_advisor.jpg`,
      topBarColor: 'u-bgColorBrandLight',
      topBarFontColor: 'u-textColorWhite',
      toggleBtn: 'advisor-toggle',
    },
    analyticsAttrs: {
      tile: 'site-selector-financial-professional',
      btn: 'site-selector-view-this-site-button',
      link: 'site-selector-website-log-in',
    },
  },
  INVESTOR: {
    welcomeMsg:
      '<span class="u-textBold">Welcome</span> to our site for individual investors',
    roleTitle: 'individual investor',
    smallicon: `${
      isLocal() ? '/cti-site' : ''
    }/webfiles/latest/assets/cti/images/icon_investor_site.svg`,
    icon: `${
      isLocal() ? '/cti-site' : ''
    }/webfiles/latest/assets/cti/images/icon_investor_illustration.svg`,
    title: 'Individual investor',
    description: 'You’re keeping track of personal investments',
    buttonLabel: 'VIEW THIS SITE',
    buttonSrc: getInvestorPathIfLocal() || '/investor',
    linkLabel: 'Account log in',
    linkSrc: `${isLocal() ? '/cti-site/cti-investorspa' : '/investor'}/login`,
    userPrefKey: 'Individual-Investors',
    style: {
      topBarBgImg: `${
        isLocal() ? '/cti-site' : ''
      }/webfiles/latest/assets/cti/images/brand-texture_banner_investor.jpg`,
      topBarColor: 'u-bgColorBrandDarker',
      topBarFontColor: 'u-textColorWhite',
      toggleBtn: 'investor-toggle',
    },
    analyticsAttrs: {
      tile: 'site-selector-individual-investor',
      btn: 'site-selector-view-this-site-button',
      link: 'site-selector-account-log-in',
    },
  },
  INSTITUTIONAL: {
    welcomeMsg:
      '<span class="u-textBold">Welcome</span> to our site for institutional investors',
    roleTitle: 'institutional investor',
    smallicon: `${
      isLocal() ? '/cti-site' : ''
    }/webfiles/latest/assets/cti/images/icon_institutional_site.svg`,
    icon: `${
      isLocal() ? '/cti-site' : ''
    }/webfiles/latest/assets/cti/images/icon_institutional_illustration.svg`,
    title: 'Institutional investor',
    description: 'You invest on behalf of institutions',
    buttonLabel: 'VIEW THIS SITE',
    buttonSrc: getInstitutionalPathIfLocal() || '/institutional',
    linkLabel: 'Account log in',
    linkSrc: '/institutional/login',
    userPrefKey: 'Institutional-Investors',
    style: {
      topBarBgImg: `${
        isLocal() ? '/cti-site' : ''
      }/webfiles/latest/assets/cti/images/brand-texture_banner_institutional.jpg`,
      topBarColor: 'u-bgColorBrandExtraLight',
      topBarFontColor: 'u-textColorBrand',
      toggleBtn: 'institutional-toggle',
    },
    analyticsAttrs: {
      tile: 'site-selector-institutional-investor',
      btn: 'site-selector-view-this-site-button',
      link: 'site-selector-account-log-in',
    },
  },
}

const stickyState = {
  isFrameBusy: false,
  scrollPosition: 190,
}

export default function PreferenceBar() {
  let siteUserType
  let isWelcomeShown
  let isSiteSelectorDismissed

  try {
    siteUserType = window.localStorage.getItem('SITE_USER_TYPE')
    isWelcomeShown = window.sessionStorage.getItem('SHOW_WELCOME')
    isSiteSelectorDismissed =
      window.sessionStorage.getItem('SITE_SELECTOR_DISMISSED') === 'TRUE'
  } catch (e) {
    console.log(e)
  }

  const stickyContainerRef = useRef()

  const [showMenu, setShowMenu] = useState(false)
  const [firstTimeVisitor, setFirstTimeVisitor] = useState(false)
  const [showWelcome, setShowWelcome] = useState(false)
  const [showConsultantTray, setShowConsultantTray] = useState(false)

  const siteSelectorRef = useRef()

  useEffect(() => {
    const _isAdvisorHomePage = isAdvisorHomePage()
    const _isInvestorHomePage = isInvestorHomePage()
    const _isInstitutionalHomePage = isInstitutionalHomePage()

    let _showMenu = false
    let _firstTimeVisitor = false
    let _showWelcome = false

    if (siteUserType) {
      if (
        (_isAdvisorHomePage && siteUserType !== 'FP-Advisor') ||
        (_isInvestorHomePage && siteUserType !== 'Individual-Investors') ||
        (_isInstitutionalHomePage && siteUserType !== 'Institutional-Investors')
      ) {
        autoRedirect()
      }
    } else {
      if (_isAdvisorHomePage) {
        _showMenu = !isSiteSelectorDismissed
        _firstTimeVisitor = !isSiteSelectorDismissed
      }
    }

    if (
      (!siteUserType && isWelcomeShown === null) ||
      (siteUserType && isWelcomeShown === 'FALSE')
    ) {
      _showWelcome = true
    }

    setShowMenu(_showMenu)
    setFirstTimeVisitor(_firstTimeVisitor)
    setShowWelcome(_showWelcome)

    if (_showWelcome && !_firstTimeVisitor) {
      try {
        window.sessionStorage.setItem('SHOW_WELCOME', 'TRUE')
      } catch (e) {
        console.log(e)
      }
    }

    window.addEventListener('load', (event) => onLoadWindow(event, _showMenu))

    window.addEventListener('scroll', debounceUpdatePosition)
    return () => {
      window.removeEventListener('load', (event) =>
        onLoadWindow(event, _showMenu)
      )

      window.removeEventListener('scroll', debounceUpdatePosition)
    }
  }, [])

  useEffect(() => {
    if (showMenu || showConsultantTray) {
      document.body.classList.add('site-selector-open')
    } else {
      document.body.classList.remove('site-selector-open')
    }
    if (showMenu || showConsultantTray) {
      setTimeout(() => {
        window.scrollTo(0, 0)
      }, 2000)
    }
  }, [showMenu, showConsultantTray])

  function onLoadWindow(e, _showMenu) {
    if (
      _showMenu &&
      window._satellite &&
      siteSelectorRef?.current?.closest &&
      siteSelectorRef?.current?.closest('.preference-bar')
    ) {
      window._satellite.track('modalEntersViewport', {
        evar21: 'site selector|open',
      })
    }
  }

  function debounceUpdatePosition() {
    if (!stickyState.isFrameBusy) {
      window.requestAnimationFrame(showStickyOnScroll)
    }
    stickyState.isFrameBusy = true
  }

  const showStickyOnScroll = () => {
    const mainHeader = document.querySelector('.MainHeader')
    const navElement = document.querySelector('.Navigation')
    const isNavigationExpanded = navElement
      ? navElement.querySelector('.nav-container.is-expanded')
      : null // check if the nav element has class is-expanded
    if (
      (!showMenu && window.scrollY > stickyState.scrollPosition) ||
      window.pageYOffset > stickyState.scrollPosition
    ) {
      stickyContainerRef.current.classList.add('sticky')
      if (mainHeader && !isNavigationExpanded) {
        mainHeader.classList.add('sticky-header')
      }
    } else {
      stickyContainerRef.current.classList.remove('sticky')
      if (mainHeader) {
        mainHeader.classList.remove('sticky-header')
      }
    }
    stickyState.isFrameBusy = false
  }

  const autoRedirect = () => {
    const urlParams = new URLSearchParams(window.location.search)

    if (urlParams.get('redirect')?.toLowerCase() === 'no') {
      return
    }

    if (siteUserType === 'FP-Advisor') {
      setTimeout(() => {
        window.location.href = getAdvisorPathIfLocal() || '/'
      }, 500)
    } else if (siteUserType === 'Individual-Investors') {
      setTimeout(() => {
        window.location.href = getInvestorPathIfLocal() || '/investor'
      }, 500)
    } else if (siteUserType === 'Institutional-Investors') {
      setTimeout(() => {
        window.location.href = getInstitutionalPathIfLocal() || '/institutional'
      }, 500)
    }
  }

  const isAdvisorHomePage = () => {
    if (
      window.location.pathname === '/' ||
      (isLocal() && window.location.pathname === '/cti-site/cti-advisor')
    ) {
      return true
    }
    return false
  }

  const isInvestorHomePage = () => {
    if (
      window.location.href.endsWith('/investor/') ||
      window.location.href.endsWith('/investor') ||
      (isLocal() && window.location.href.endsWith('/cti-site/cti-investor'))
    ) {
      return true
    }
    return false
  }

  const isInstitutionalHomePage = () => {
    if (
      window.location.href.endsWith('/institutional/') ||
      window.location.href.endsWith('/institutional') ||
      (isLocal() &&
        window.location.href.endsWith('/cti-site/cti-institutional'))
    ) {
      return true
    }
    return false
  }

  const clickToRedirect = (type, redirectLink) => {
    try {
      window.localStorage.setItem('SITE_USER_TYPE', DATA[type].userPrefKey)
    } catch (e) {
      console.log(e)
    }

    if (siteUserType !== DATA[type].userPrefKey) {
      try {
        window.sessionStorage.setItem('SHOW_WELCOME', 'FALSE')
      } catch (e) {
        console.log(e)
      }
    }

    let shouldRedirect = false
    switch (type) {
      case 'ADVISOR':
        if (isInvestor() || isInstitutional()) {
          shouldRedirect = true
        }
        break
      case 'INVESTOR':
        if (!isInvestor()) {
          shouldRedirect = true
        }
        break
      case 'INSTITUTIONAL':
        if (!isInstitutional()) {
          shouldRedirect = true
        }
        break
    }

    if (shouldRedirect) {
      setTimeout(() => {
        window.location.href = redirectLink
      }, 500)
    } else {
      setShowMenu(false)
      setFirstTimeVisitor(false)
      try {
        window.sessionStorage.setItem('SHOW_WELCOME', 'TRUE')
      } catch (e) {
        console.log(e)
      }
    }
  }

  const linkToRedirect = (type, redirectLink) => {
    try {
      window.localStorage.setItem('SITE_USER_TYPE', DATA[type].userPrefKey)
    } catch (error) {
      console.log(error)
    }

    setTimeout(() => {
      window.location.href = redirectLink
    }, 500)

    setShowMenu(false)
    setFirstTimeVisitor(false)
  }

  const toggleMenu = (e) => {
    setShowMenu(!showMenu)

    setShowConsultantTray(false)
    e.currentTarget?.blur()
  }

  const handleClose = (e) => {
    setFirstTimeVisitor(false)
    setShowMenu(false)

    try {
      window.sessionStorage.setItem('SHOW_WELCOME', 'TRUE')

      window.sessionStorage.setItem('SITE_SELECTOR_DISMISSED', 'TRUE')
    } catch (e) {
      console.log(e)
    }

    e.currentTarget?.blur()
  }

  const renderTiles = (type, show, index) => {
    return (
      <div
        className={`${
          show ? 'show option-' + index : ''
        } site-option u-size1of3 u-sm-sizeFull u-flex u-flexCol u-flexAlignItemsCenter u-sm-flexJustifyCenter`}
        data-analytics={DATA[type].analyticsAttrs.tile}
      >
        <img className="site-icon u-sm-hidden" src={DATA[type].icon} alt="" />
        <h3 className="u-paddingTop2gu u-sm-paddingTop0 u-textColorBrand">
          {DATA[type].title}
        </h3>
        <p className="u-textSizeMsr u-textColorDefault u-paddingTop1gu u-paddingBottom4gu u-sm-textSizeMsrm1 u-sm-paddingTop1gu u-sm-paddingBottom2gu">
          {DATA[type].description}
        </p>
        <div className="u-paddingBottom2gu u-textSizeMsrm1">
          <button
            className="Button Button--secondary u-textColorBrand"
            data-qa-automation={`site-selector-${type.toLowerCase()}-site`}
            onClick={() => clickToRedirect(type, DATA[type].buttonSrc)}
            data-analytics={DATA[type].analyticsAttrs.btn}
          >
            {DATA[type].buttonLabel}
          </button>
        </div>
        <div className="u-textSizeMsrm1">
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid, jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions  -- CTINEXT-4701 */}
          <a
            className="Link u-textSecondary u-textSizeMsr"
            onClick={() => linkToRedirect(type, DATA[type].linkSrc)}
            data-analytics={DATA[type].analyticsAttrs.link}
          >
            {DATA[type].linkLabel}
          </a>
        </div>
      </div>
    )
  }

  const toggleConsultant = (e) => {
    setShowMenu(false)

    setShowConsultantTray((prev) => !prev)

    e.currentTarget?.blur()
  }

  const type = isInvestor()
    ? 'INVESTOR'
    : isInstitutional()
      ? 'INSTITUTIONAL'
      : 'ADVISOR'

  return (
    <div
      className={`site-selector ${showMenu ? 'expanded' : ''} ${
        showConsultantTray && isLoggedIn ? 'expand-consultant-tray' : ''
      }`}
      ref={siteSelectorRef}
    >
      <div
        ref={stickyContainerRef}
        className={`tray ${showMenu || showConsultantTray ? 'overlay' : ''}`}
      >
        <div className={`sticky-bar ${DATA[type].style.topBarColor}`}></div>
        <div className="u-bgColorWhite content">
          <div
            role="region"
            aria-label="Banner"
            className={`top-bar u-flex u-flexAlignItemsCenter ${
              showWelcome || firstTimeVisitor ? 'wide-bar' : ''
            } ${DATA[type].style.topBarColor}`}
            style={{
              backgroundImage: showWelcome
                ? 'url(' + DATA[type].style.topBarBgImg + ')'
                : '',
            }}
          >
            <div className="banner-content u-sizeConstrained u-flex u-textColorWhite u-flexAlignItemsCenter">
              {firstTimeVisitor ? (
                <div className="u-size5of6 u-sm-sizeFull u-flex u-flexAlignItemsCenter u-sm-flexJustifyCenter">
                  <div
                    className="u-textSecondary u-textSizeMsrp2 u-sm-textSizeMsr u-textColorWhite u-sm-textCenter"
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(
                        DATA.FIRST_TIME_VISITOR.welcomeMsg
                      ),
                    }}
                  />
                </div>
              ) : showWelcome ? (
                <div className="u-sizeZero u-sm-sizeFull u-flex u-flexAlignItemsCenter animate-message">
                  <div
                    className={`message u-textSecondary u-textSizeMsrp2 u-sm-textSizeMsr u-sm-sizeFull ${DATA[type].style.topBarFontColor}`}
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(DATA[type].welcomeMsg),
                    }}
                  />
                  <button
                    className={`Button Button--tertiary u-textNoTransform u-padding1gu u-hidden u-sm-block ${DATA[type].style.topBarFontColor} ${DATA[type].style.toggleBtn}`}
                    onClick={(e) => toggleMenu(e)}
                    data-analytics="site-selector-dropdown"
                  >
                    {showMenu ? (
                      <span
                        className={`arrow Icon--material u-textSizeMsrp2 u-posRelative ${DATA[type].style.topBarFontColor}`}
                      >
                        expand_less
                      </span>
                    ) : (
                      <span
                        className={`arrow Icon--material u-textSizeMsrp2 u-posRelative ${DATA[type].style.topBarFontColor}`}
                      >
                        expand_more
                      </span>
                    )}
                  </button>
                </div>
              ) : (
                ''
              )}

              <div
                className={`u-flex  ${
                  firstTimeVisitor
                    ? 'u-flexJustifyEnd u-size1of6 u-sm-sizeZero'
                    : showWelcome
                      ? 'u-flexJustifyStart u-size1of2 u-sm-hidden'
                      : 'u-flexJustifyStart u-size1of2 u-sm-sizeFull'
                }`}
              >
                {firstTimeVisitor && showMenu ? (
                  <button
                    className="close-btn Button"
                    onClick={(e) => handleClose(e)}
                    data-analytics="site-selector-close"
                  >
                    <span className="icon Icon--material u-textColorWhite">
                      close
                    </span>
                  </button>
                ) : (
                  <button
                    className={`Button Button--tertiary u-textNoTransform u-flex u-flexAlignItemsCenter u-padding1gu u-sm-marginRight3gu site-toggle-btn u-sm-sizeFull ${
                      DATA[type].style.topBarFontColor
                    } ${DATA[type].style.toggleBtn} ${
                      showWelcome ? 'animate-btn ' : ''
                    }`}
                    onClick={(e) => toggleMenu(e)}
                    data-analytics="site-selector-dropdown"
                    data-qa-automation="site-selector-dropdown"
                    data-testid="site-selector-dropdown"
                  >
                    <img
                      className="u-marginRight1gu"
                      src={DATA[type].smallicon}
                      alt=""
                    />
                    {DATA[type].roleTitle && (
                      <>
                        <span className="u-textPrimary u-marginRight1gu">
                          I am a U.S.{' '}
                        </span>
                        <span
                          className="u-textBold u-textDecoUnderline"
                          data-testid="role-title"
                        >
                          {DATA[type].roleTitle}
                        </span>
                      </>
                    )}
                    {showMenu ? (
                      <span
                        className={`arrow Icon--material u-textSizeMsrp2 u-posRelative u-posRelative ${DATA[type].style.topBarFontColor}`}
                      >
                        expand_less
                      </span>
                    ) : (
                      <span
                        className={`arrow Icon--material u-textSizeMsrp2 u-posRelative u-posRelative ${DATA[type].style.topBarFontColor}`}
                      >
                        expand_more
                      </span>
                    )}
                  </button>
                )}
              </div>

              {!firstTimeVisitor && (
                <div className="u-flex u-flexJustifyEnd u-size1of2 u-sm-hidden consultant-btn-container">
                  {type === 'ADVISOR' && (
                    <div className="u-flex u-flexAlignItemsCenter  u-flexJustifyEnd u-marginRight1gu">
                      <button
                        className="Button Button--secondary u-textNoTransform site-toggle-btn u-textColorWhite advisor-toggle u-padding1gu u-flex u-flexAlignItemsCenter"
                        onClick={toggleConsultant}
                        data-qa-automation="consultant-info-dropdown"
                        data-analytics={
                          isLoggedIn()
                            ? 'contact-my-sales-consultant-button'
                            : 'find-my-sales-consultant-button'
                        }
                      >
                        <span className="u-textBold u-textDecoUnderline">
                          {isLoggedIn()
                            ? 'Contact my sales consultants'
                            : 'Find my sales consultants'}
                        </span>
                        {showConsultantTray ? (
                          <span className="arrow Icon--material u-textSizeMsrp2 u-textColorWhite u-posRelative">
                            expand_less
                          </span>
                        ) : (
                          <span className="arrow Icon--material u-textSizeMsrp2 u-textColorWhite u-posRelative">
                            expand_more
                          </span>
                        )}
                      </button>
                    </div>
                  )}
                  <UserLoginInfo type={type} />
                </div>
              )}
            </div>
          </div>
          <div className={`options ${showMenu ? 'site-options' : ''}`}>
            <div className="u-flex u-sm-flexWrap u-sizeConstrained u-padding8gu u-sm-paddingTop0 u-sm-paddingBottom0 u-textCenter u-sm-flexJustifyCenter">
              {renderTiles('ADVISOR', showMenu, 1)}
              {renderTiles('INVESTOR', showMenu, 2)}
              {renderTiles('INSTITUTIONAL', showMenu, 3)}
            </div>
            <div className="last-option u-borderTop u-borderColorNeutralLight">
              <div className="u-flex u-flexJustifyCenter u-sizeConstrained u-padding3gu u-sm-paddingTop4gu u-sm-paddingBottom4gu">
                <a
                  className="Link u-textSecondary u-textSizeMsr"
                  href="https://www.columbiathreadneedle.com/en"
                  data-analytics="site-selector-outside-us-link"
                >
                  I am outside the U.S.
                </a>
              </div>
            </div>
          </div>
          {type === 'ADVISOR' ? (
            isLoggedIn() ? (
              <LoggedInTray show={showConsultantTray} />
            ) : (
              <LoggedOutTray show={showConsultantTray} />
            )
          ) : (
            ''
          )}
        </div>
      </div>
    </div>
  )
}

function UserLoginInfo({ type }) {
  const [user, setUser] = useState(null)
  const [expanded, setExpanded] = useState(false)

  useEffect(() => {
    getAuthManagerInstance()
      .getProfileServiceBehaviorSubject()
      .subscribe((profile) => {
        if (profile) {
          setUser(profile)
        }
      })
  }, [])

  const logOffClick = () => {
    logOff()

    setUser(null)
    setExpanded(false)
  }

  const expandMenu = (e) => {
    setExpanded((prev) => !prev)

    e.preventDefault()
    e.stopPropagation()
  }

  const advisorLoginInfo = () => {
    return (
      <div className="UserLoginInfo">
        {user ? (
          <div className={`CtiDropdown ${expanded ? 'is-expanded' : ''}`}>
            <button
              onClick={expandMenu}
              className="CtiDropdown-toggle login-btn Button Button--primary u-flex u-flexAlignItemsCenter"
              type="button"
              id="secondary-dropdown"
              aria-haspopup={expanded}
              aria-expanded={expanded}
              data-qa-automation="header-userinfo"
            >
              {user.preferredFirstNm}
            </button>

            <ul
              className="CtiDropdown-menu u-textColorBrandLight u-bgColorWhite u-borderColorNeutralLight u-borderBottom u-borderTop u-borderLeft u-borderRight u-posAbsolute u-paddingBottom1gu u-textLeft"
              aria-labelledby="secondary-dropdown"
            >
              <li className="u-textSizeMsr u-textPrimary CtiDropdown-item">
                <a
                  className="CtiDropdown-link u-block u-padding2gu u-textNoWrap"
                  href="/my-profile/"
                  data-qa-automation="header-userinfo-profile"
                >
                  My Profile
                </a>
              </li>
              <li className="u-textSizeMsr u-textPrimary CtiDropdown-item">
                <a
                  className="CtiDropdown-link u-block u-padding2gu u-textNoWrap"
                  href="/subscribe/"
                  data-qa-automation="header-userinfo-subscribe"
                >
                  Email Subscriptions
                </a>
              </li>
              <li className="u-textSizeMsr u-textPrimary CtiDropdown-item">
                <a
                  onClick={logOffClick}
                  className="CtiDropdown-link u-block u-padding2gu logout-button u-textNoWrap"
                  href="/login?logout=true"
                  data-qa-automation="advisor-log-out"
                >
                  Log Out
                </a>
              </li>
            </ul>
          </div>
        ) : (
          <a
            className="login-btn Button Button--primary u-flex u-flexAlignItemsCenter"
            href={DOMPurify.sanitize(`${getAbsolutePath(true)}/login`)}
            data-qa-automation="advisor-login"
            data-analytics="advisor-login-button"
          >
            Log In
          </a>
        )}
      </div>
    )
  }

  const investorLoginInfo = () => {
    return (
      <div className="UserLoginInfo">
        <a
          className="login-btn Button Button--primary u-flex u-flexAlignItemsCenter u-bgColorBrandLight"
          href={DOMPurify.sanitize(
            `${isLocal() ? '/cti-site/cti-investorspa' : '/investor'}/login`
          )}
          data-qa-automation="investor-login"
        >
          Log In
        </a>
      </div>
    )
  }

  const institutionalLoginInfo = () => {
    const userData = accessStorage.getUserInfo()
    return (
      <div className="UserLoginInfo institutionalLogin">
        {isInstitutionalLoggedIn() ? (
          <div className={`CtiDropdown ${expanded ? 'is-expanded' : ''}`}>
            <button
              onClick={expandMenu}
              className="CtiDropdown-toggle login-btn Button Button--primary u-flex u-flexAlignItemsCenter"
              type="button"
              id="secondary-dropdown"
              aria-haspopup={expanded}
              aria-expanded={expanded}
              data-qa-automation="header-userinfo"
            >
              {userData?.fullName}
            </button>
            <ul
              className="CtiDropdown-menu u-textColorBrandLight u-bgColorWhite u-borderColorNeutralLight u-borderBottom u-borderTop u-borderLeft u-borderRight u-posAbsolute u-paddingBottom1gu u-textLeft"
              aria-labelledby="secondary-dropdown"
            >
              <li className="u-textSizeMsr u-textPrimary CtiDropdown-item">
                <a
                  className="CtiDropdown-link u-block u-padding2gu u-textNoWrap no-lock-icon"
                  href={DOMPurify.sanitize(
                    `${getAbsolutePath(true)}/secure/change-password/`
                  )}
                  data-qa-automation="header-userinfo-change-password"
                >
                  Change Password
                </a>
              </li>
              <li className="u-textSizeMsr u-textPrimary CtiDropdown-item">
                <a
                  className="CtiDropdown-link u-block u-padding2gu u-textNoWrap no-lock-icon"
                  href={DOMPurify.sanitize(
                    `${getAbsolutePath(true)}/secure/your-reports`
                  )}
                  data-qa-automation="header-userinfo-your-reports"
                >
                  Your Reports
                </a>
              </li>
              <li className="u-textSizeMsr u-textPrimary CtiDropdown-item">
                <a
                  onClick={logoutInstitutional}
                  className="CtiDropdown-link u-block u-padding2gu logout-button u-textNoWrap no-lock-icon"
                  href={DOMPurify.sanitize(`${getAbsolutePath(true)}/logout`)}
                  data-qa-automation="institutional-log-out"
                >
                  Log Out
                </a>
              </li>
            </ul>
          </div>
        ) : (
          <a
            className="login-btn Button Button--primary u-flex u-flexAlignItemsCenter"
            href={DOMPurify.sanitize(`${getAbsolutePath(true)}/login`)}
            data-qa-automation="institutional-login"
            data-analytics="institutional-login-button"
          >
            Log In
          </a>
        )}
      </div>
    )
  }

  const renderLoginInfo = () => {
    switch (type) {
      case 'ADVISOR':
        return advisorLoginInfo()
      case 'INVESTOR':
        return investorLoginInfo()
      case 'INSTITUTIONAL':
        return institutionalLoginInfo()
    }
  }

  return <>{renderLoginInfo()}</>
}
